import { gql, useApolloClient } from '@apollo/client';
import { useCallback, useMemo, useState } from 'react';
import type { EventsSocketMessage } from '../../store';
import { useSocketFeed } from '../../store';
import type { QuickSwitchEvent, RawQuickSwitchEvent } from './event';
import { isShownQuickSwitchEvent, mapQuickSwitchEvent, QUICK_SWITCH_EVENT_FIELDS, QUICK_SWITCH_FETCH_POLICY, QUICK_SWITCH_LIMIT, recallTotal, rememberTotal } from './event';

const LIVE_EVENTS = gql`
  query QuickSwitchLiveEvents($input: GetEventsInput!) {
    getEvents(input: $input) {
      total
      result {
        ...QuickSwitchEventFields
      }
    }
  }
  ${QUICK_SWITCH_EVENT_FIELDS}
`;

interface QueryResult {
  getEvents?: {
    total?: number | null;
    result: (RawQuickSwitchEvent | null)[];
  } | null;
}

interface QueryVars {
  input: {
    status: number;
    page: number;
    limit: number;
  };
}

const SCOPE = 'quick-switch:live';

/* Event.Phase.Live — the scheduler status code for a live event. */
const LIVE_STATUS = 2;

/* The same code as a string, which is how the socket reports it. */
const LIVE_STATUS_CODE = String(LIVE_STATUS);

/*
 * Keeps the feed current from the batch itself: an event whose status went to live is added, one that
 * left it is dropped, and a deleted one is removed. Returns `undefined` — refetch instead — whenever a
 * changed id arrives without its `events[]` entry, which is every frame of the older, id-only shape.
 *
 * An event that first appears here is built from what the frame carries, so it has no thumbnail and no
 * priority dot until the next full fetch. The server does not send those yet.
 */
const patchLive = (
  rows: RawQuickSwitchEvent[],
  message: EventsSocketMessage
): RawQuickSwitchEvent[] | undefined => {
  if (message.kind !== 'events' || message.ids.some((id) => !message.details.has(id))) {
    return undefined;
  }

  const removed = new Set(message.removedIds.map(String));
  const next = rows.filter((row) => !removed.has(row.eid));

  message.details.forEach((event) => {
    const index = next.findIndex((row) => row.eid === event.id);

    if (event.status !== LIVE_STATUS_CODE) {
      if (index >= 0) {
        next.splice(index, 1);
      }

      return;
    }

    const known = index >= 0 ? next[index] : undefined;

    const row: RawQuickSwitchEvent = {
      ...known,
      eid: event.id,
      name: event.title ?? known?.name,
      isLive: true,
      has_asset: true,
      muxStreamUrl: event.downloadUrl ?? known?.muxStreamUrl,
      client: {
        ...known?.client,
        organizationName: event.organization_name ?? known?.client?.organizationName
      }
    };

    if (index >= 0) {
      next[index] = row;
    } else {
      next.unshift(row);
    }
  });

  return next;
};

export const useQuickSwitchLiveEvents = (): {
  events: QuickSwitchEvent[];
  loading: boolean;
  total?: number;
} => {
  const client = useApolloClient();
  const [total, setTotal] = useState(() => recallTotal(SCOPE));

  const fetchLive = useCallback(async (): Promise<RawQuickSwitchEvent[]> => {
    const { data } = await client.query<QueryResult, QueryVars>({
      query: LIVE_EVENTS,
      variables: { input: { status: LIVE_STATUS, page: 1, limit: QUICK_SWITCH_LIMIT } },
      fetchPolicy: QUICK_SWITCH_FETCH_POLICY
    });

    rememberTotal(SCOPE, data.getEvents?.total);
    setTotal(data.getEvents?.total ?? undefined);

    return (data.getEvents?.result ?? []).filter(isShownQuickSwitchEvent);
  }, [client]);

  /*
   * Frames that carry their events keep this feed up to date on their own, so after the first load it
   * makes no requests at all. Frames without them still cost one: whether an event just went live
   * cannot be told from an id.
   */
  const { rows, loading } = useSocketFeed({
    scope: SCOPE,
    cacheKey: 'live',
    enabled: true,
    fetch: fetchLive,
    patch: patchLive
  });

  const events = useMemo(() => rows.map(mapQuickSwitchEvent), [rows]);

  return { events, loading, total };
};
